import {CommonModule, formatDate, isPlatformBrowser, isPlatformServer, ViewportScroller} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DOCUMENT,
  Inject,
  inject,
  OnInit,
  PLATFORM_ID,
  signal
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {Meta} from '@angular/platform-browser';
import {PAYMENT_TYPE, SERVICE_PRODUCT} from '@shared/enum/Enum';
import {Page} from '@shared/models/page';
import {RegisterPurchase} from '@shared/models/register-purchase';
import {NavigationService} from './pages/funnel/service/navigation.service';
import {AuthService} from './services/auth.service';
import {ConsentOrchestratorService} from './services/consent-orchestrator.service';
import {ContentService} from './services/content.service';
import {FunnelService} from './services/funnel.service';
import {StartupService} from './services/startup.service';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {RestService} from "./services/rest.service";
import {APP_CONFIG, AppConfig} from "./app-config.model";
import {UserService} from "./services/user.service";
import {filter, map, startWith} from "rxjs/operators";
import {fromEvent} from "rxjs";
import {GenericService} from "./services/generic.service";
import {StickyBarComponent} from "@shared/components/mobile/sticky-bar/sticky-bar.component";
import {GtmDatalayerService} from "./services/gtm-datalayer.service";
import {LoadingCoordinatorService, LoadingOperation} from './services/loading-coordinator.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, StickyBarComponent, RouterOutlet, ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly configEnv = inject<AppConfig>(APP_CONFIG);
  private readonly loadingCoordinator = inject(LoadingCoordinatorService);
  rest = inject(RestService);
  userService = inject(UserService);
  private slug: string;
  public isLeftToggleSelected: boolean;
  public switchShow: boolean;
  slugChat: string;
  page: Page;

  private readonly scrollPastThreshold = isPlatformBrowser(this.platformId)
    ? toSignal(
      fromEvent(this.document, 'scroll').pipe(
        map(() => (this.viewport.getScrollPosition()?.[1] ?? 0) > 1250),
        startWith(false)
      ),
      { initialValue: false }
    )
    : signal(false);

  readonly showSticky = signal(false);

  readonly showStickyBar = computed(() => {
    const bannerMagnolia = this.document.getElementsByClassName('bottom-banner')[0];
      if (this.scrollPastThreshold() && this.showSticky() && this.genericService.isMobile()) {
        bannerMagnolia?.setAttribute('style', 'bottom: 71px !important');
        return true;
      }
      bannerMagnolia?.removeAttribute('style');
      return false;
    }
  );

  private isStickyAllowedPath(pathname: string): boolean {
    const pathParts = pathname.split('/').filter(Boolean);
    const segment = pathParts[0];
    return segment !== 'funnel'
      && segment !== 'area-riservata'
      && segment !== 'chi-siamo'
      && segment !== 'contatti'
      && segment !== 'partners'
      && segment !== 'azienda'
      && segment !== 'set-informativi'
      && segment !== 'reclami'
      && segment !== 'account'
      && segment !== 'lavora-con-noi'
      && segment !== 'crisi-iran-medio-oriente'
      && segment !== 'europ-assistance-diventa-redion'
      && (segment !== 'contenuti-utili' || pathParts[1] !== undefined);
  }

  private updateStickyVisibility(url?: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.showSticky.set(false);
      return;
    }

    const path = decodeURIComponent((url ?? this.router.url).split('?')[0] || '/');
    this.showSticky.set(this.isStickyAllowedPath(path));
  }


  constructor(private metaTagService: Meta,
              private funnelService: FunnelService,
              private router: Router,
              public genericService: GenericService,
              private navigationService: NavigationService,
              private startupService: StartupService,
              private authService: AuthService,
              private contentService: ContentService,
              @Inject(DOCUMENT) private document: Document,
              private consentOrchestrator: ConsentOrchestratorService,
              private gtmDatalayerService: GtmDatalayerService,
              private viewport: ViewportScroller,
            ) {

    this.metaTagService.addTags([
      { name: 'keywords', content: 'Assicurazione, Viaggi, Carro attrezzi' },
      { name: 'author', content: 'Redion' }
    ]);

  }



  loadMyClinic(f, p, discount, customerId?) {
    const loading = this.loadingCoordinator.begin({kind: 'calculation', data: {
      title: 'Stiamo riaprendo la tua proposta'
    }});
    let obj: any = {
      formula: f,
      product: p,
      tc: discount || ''
    };
    const user = this.authService.getUser();

    if (user) {
      obj.customerId = (user as any)?.Id;
      obj.customerType = user.LogicalName;
    }


    if (customerId){
      obj.QSCustomerId = customerId;
    }

    this.funnelService.quotationMyClinic(obj)
      .toPromise()
      .then(async (res) => {
        const products = [];
        const Request: any = {};
        products.push(res);

        Request.products = products;
        Request.payload = {
          form: {
            paymentMethod: {
              id: p
            },
            subscription: {
              id: f
            }
          }
        };
        return this.navigationService.setStoredItem('Request', Request)
          .then(() => {
            return this.router.navigate(['/funnel/offer']);
          })
          .finally(() => loading.finish());
      })
      .catch(() => loading.finish());
  }

  loadDigitaleSicuro(f, discount) {
    const loading = this.loadingCoordinator.begin({kind: 'calculation', data: {
      title: 'Stiamo riaprendo la tua proposta'
    }});
    const user = this.authService.getUser()
    const obj: any = {
      formula: f,
      tc: discount || ''
    };
    if (user) {
      obj.customerId = (user as any)?.Id;
      obj.customerType = user.LogicalName;
    }

    this.funnelService.quotationDigitaleSicuro(obj)
      .toPromise()
      .then(async (res) => {
        const products = [];
        const Request: any = {};
        products.push(res);

        Request.products = products;
        Request.payload = {
          form: {
            paymentMethod: {
              id: f
            }
          }
        };
        return this.navigationService.setStoredItem('Request', Request)
          .then(() => {
            return this.router.navigate(['/funnel/offer']);
          })
          .finally(() => loading.finish());
      })
      .catch(() => loading.finish());
  }

  loadQuoting(rate: any, tc: any, pdv: any, monthly?: any) {
    const user = this.authService.getUser();
    const loading = this.loadingCoordinator.begin({kind: 'calculation', data: {
      title: 'Stiamo riaprendo la tua proposta'
    }});
    const products = [];
    const Request: any = {};
    const date = [];

    const obj: any = {
      tc: tc,
      ratenum: rate,
      index: 0,
      monthly: monthly !== undefined ? monthly : ''
    };
    if (user) {
      obj.customerId = (user as any).Id;
      obj.customerType = user.LogicalName;
    }

    this.funnelService.quotation({}, obj)
      .toPromise()
      .then(async (res) => {
        this.switchShow = res.rate_has_monthly_payment;
        this.isLeftToggleSelected = res.rate_month_amounts != null;
        products.push(res);
        for (let z = 0; z < res.rates - 1; z++) {
          products.push(null);
        }
        return this.funnelService.getCacheRate({ rateNumber: rate })
          .toPromise()
          .then(v => {
            Request.products = products;
            Request.ratenum = res.rate_num;
            Request.rates = res.rates;
            Request.payload = v.rate.payload;
            Request.category = v.rate.category;
            Request.category.pdvCode = pdv;
            Request.hasPromo = res.rate_has_promo;
            return this.navigationService.setStoredItem('Request', Request)
              .then(() => {
                return this.router.navigate(['/funnel/offer']);
              })
              .finally(() => loading.finish());
          }, ( response ) => {
            if (response?.error?.includes('not found')) {
              //GET policy/quotationrate
              this.funnelService.getQuotation( obj.ratenum )
                .toPromise()
                .then( ( resQuote ) => {
                  const insurance_id = resQuote.insurance_id;

                  switch ( insurance_id ) {
                    case '223':
                      let startDate = '12587';
                      let endDate = '12588';
                      let destinationId = '12592';
                      let destinationTitle = '12591';
                      let noMaggiore70anni = '12575';
                      let siMaggiore70anni = 12575;
                      let noMinore70anni = '12565';
                      let siMinore70anni = 12565
                      this.slugChat = '/prodotti-per-te/viaggi/viaggi-europa';
                      this.payloadFromChatBot( products, obj, pdv, resQuote, Request, date, startDate, endDate, noMaggiore70anni, siMaggiore70anni, noMinore70anni, siMinore70anni, loading, destinationId, destinationTitle );
                      break;
                    case '92':
                      if ( resQuote.insurance_title === 'Viaggi Italia' ) {
                        let startDate = '12320';
                        let endDate = '12321';
                        let noMaggiore70anni = '12281';
                        let siMaggiore70anni = 12281;
                        let noMinore70anni = '12270';
                        let siMinore70anni = 12270;
                        this.slugChat = '/prodotti-per-te/viaggi/viaggi-italia';
                        this.payloadFromChatBot( products, obj, pdv, resQuote, Request, date, startDate, endDate, noMaggiore70anni, siMaggiore70anni, noMinore70anni, siMinore70anni, loading );
                      } else {
                        let startDate = '12252';
                        let endDate = '12253';
                        let destinationId = '12258';
                        let destinationTitle = '12257';
                        let noMaggiore70anni = '12240';
                        let siMaggiore70anni = 12240;
                        let noMinore70anni = '12230';
                        let siMinore70anni = 12230
                        this.slugChat = '/prodotti-per-te/viaggi/viaggi-nostop-vacanza';
                        this.payloadFromChatBot( products, obj, pdv, resQuote, Request, date, startDate, endDate, noMaggiore70anni, siMaggiore70anni, noMinore70anni, siMinore70anni, loading, destinationId, destinationTitle );
                      }
                      break;
                    default:
                      loading.finish();
                      break;
                  }
                } )
                .catch(() => loading.finish());
            } else {
              loading.finish();
            }
          } );

      }, (err) => {
        loading.finish();
        if (err.error.ModelState) {
          if (err.error.ModelState[''][0]) {
            setTimeout(()=>{
              this.router.navigate(['/funnel/policy-expired-date'], {queryParams: {ratenum: obj.ratenum}});
            },200);
          }
        } else {
          this.router.navigate(['/'], {onSameUrlNavigation: 'reload'});
        }

      });
  }

  payloadFromChatBot(products, obj, pdv, resQuote, Request, date, startDate, endDate, noMaggiore70anni, siMaggiore70anni,
                     noMinore70anni, siMinore70anni, loading: LoadingOperation, destinationId?, destinationTitle?){
    date.push(formatDate(resQuote.values[startDate], 'DD/MM/YYYY', 'it'));
    date.push(formatDate(resQuote.values[endDate], 'DD/MM/YYYY', 'it'));
    let destination = {};
    if (resQuote.insurance_title !== 'Viaggi Italia'){
      destination = {
        id: resQuote.values[destinationId],
        title: resQuote.values[destinationTitle]
      };
    }else{
      destination = {
        id: 115,
        title: 'ITALIA'
      };
    }
    let maggiore70anni;
    if (resQuote.values[noMaggiore70anni]){
       maggiore70anni = {
        description: 'più di 70 anni',
        id: 2,
        value: 0
      };
    }else{
      for (let i = 1; i <= 9; i++ ){
        if (resQuote.values[siMaggiore70anni + i]){
          maggiore70anni = {
            description: 'più di 70 anni',
            id: 2,
            value: Number( resQuote.values[siMaggiore70anni + i] )
          };
          break;
        }
      }
    }
    let minore70anni;
    if ( resQuote.values[noMinore70anni] ) {
      minore70anni = {
        description: 'Da 0 a 70 anni',
        id: 1,
        value: 0
      };
    }
    else {
      for ( let i = 1; i <= 9; i++ ) {
        if ( resQuote.values[siMinore70anni + i] ) {
          minore70anni = {
            description: 'Da 0 a 70 anni',
            id: 1,
            value: Number( resQuote.values[siMinore70anni + i] )
          };
          break;
        }
      }
    }
    Request = {
      payload: {
        date: [],
        form: {
          destination: destination,
          maggiore70anni: maggiore70anni,
          minore70anni: minore70anni
        }
      }
    };
    Request.payload['date'] = date;
    Request.products = products;
    Request.rates = 3;
    Request.ratenum = obj.ratenum;

      this.contentService.getPage(this.slugChat).subscribe(
        (resPage: Page) => {
          this.page = resPage;
          Request.category=resPage.sezioni[0].schedaProdoto.preventivatore.sottocategoria;
          Request.category.pdvCode = pdv;
          this.navigationService.setStoredItem('Request', Request)
            .then(() => {
              return this.router.navigate(['/funnel/offer']);
            })
            .finally(() => loading.finish())
            .catch(() => loading.finish());
          },
          (err) => {
            loading.finish();
            //this.stateService.go((this.genericService.isDesktop() ? "View" : "Mobile") + ".404");
          }
        );
  }

  registerPurchase(value: any, navigate?: boolean, amazontoken?: string): Promise<void> {

    let initialQuote = this.navigationService.getStoredItem('initialQuote');
    console.log(initialQuote)

    let isAcquistaFlag = false;
    if (this.navigationService.getStoredItem('request')) {
      if (this.navigationService.getStoredItem('request').length) {
        isAcquistaFlag = this.navigationService.getStoredItem('request').reduce((acc, r) => r.isAcquista || acc, false);
      }
      else {
        isAcquistaFlag = this.navigationService.getStoredItem('request').isAcquista;
      }
    }
    if (isAcquistaFlag) {
      initialQuote = this.navigationService.getStoredItem('request').prepareQuotation;
    }

    let request;
    let renewFlag = false;
    if (this.navigationService.getStoredItem('request')) {
      if (this.navigationService.getStoredItem('request').length) {
        renewFlag = this.navigationService.getStoredItem('request').reduce((acc, r) => r.renew || acc, false);
      }
      else {
        renewFlag = this.navigationService.getStoredItem('request').renew;
      }
    }
    if (renewFlag) {
      request = {
        policyNumber: this.navigationService.getStoredItem('request').policy.number,
        paymentMethod: PAYMENT_TYPE[this.navigationService.getStoredItem('payment').payment],
        paymentSystemParams: value,
        amazonAccessToken: amazontoken,
        renew: true
      };
      if (this.navigationService.getStoredItem('payment') && this.navigationService.getStoredItem('payment').orderReferenceId) {
        request.amazonOrderReferenceId = this.navigationService.getStoredItem('payment').orderReferenceId;
      }
    } else {
      if (this.navigationService.getStoredItem('payment')){
        request = new RegisterPurchase(initialQuote,
          this.navigationService.getStoredItem('payment').payment,
          value, amazontoken, this.navigationService.getStoredItem('payment').orderReferenceId, this.navigationService.getStoredItem('payment').event);
      }
    }

    if (SERVICE_PRODUCT.includes(this.navigationService.getStoredItem('request') &&
      this.navigationService.getStoredItem('request').category?.codiceQuestionarioSottocategoria)) {
      request.context = request.contexts[0];
      return this.funnelService.registerSubscriptionPurchasing(request)
        .toPromise()
        .then(() => {
          this.router.navigate(['/funnel/summary']);
        }, (err) => {
          if (err.error.ModelState) {
            if (err.error.ModelState[''][0]) {
              setTimeout(() => {
                this.router.navigate(['/funnel/policy-already-issued']);
              }, 200);
            }
          } else {
            this.router.navigate(['/']);
          }

        });
    } else {
      return this.funnelService.registerPurchasing(request)
        .toPromise()
        .then(async (res) => {
          if (this.navigationService.getStoredItem('request').renew) {
            const r = this.navigationService.getStoredItem('request');
            r.orderNumber = res;
            await this.navigationService.setStoredItem('Request', r);
          }
          this.router.navigate(['/funnel/summary'], {onSameUrlNavigation: 'reload'});

        }, (err) => {
          if (err.error.ModelState)
          {
            if (err.error.ModelState['PolicyAlreadyIssued']) {
              setTimeout(() => {
                this.router.navigate(['/funnel/policy-already-issued']);
              },200);

            }

            if (err.error.ModelState['UnauthorizedTransaction'] || err.error.ModelState[''][0]==='UnexecutedTransaction') {
              setTimeout(()=>{
                this.router.navigate(['/funnel/funnel/unauthorized-transaction']);

              },200);
            }
            if(err.error.ModelState[''][0]==='NotFound'){
              setTimeout(()=>{
                this.router.navigate(['/funnel/payment']);
             },200);
            }
          } else {
            //this.stateService.go(this.genericService.viewRoot() + '.ContentPage', { slugPath: '/' }, {reload: true, notify:true });
          }

        });
    }

  }

  private async handlePaymentReturn(
    value: any,
    errorContext: string,
    isAmazonPay = false,
  ): Promise<void> {
    const loading = this.loadingCoordinator.begin({kind: 'operation', delayMs: 0});
    try {
      await this.navigationService.getCacheData();
      const amazonToken = isAmazonPay
        ? this.navigationService.getStoredItem('amazonToken').amazonToken
        : undefined;
      await this.registerPurchase(value, isAmazonPay, amazonToken);
    } catch (error) {
      console.debug(errorContext, error);
    } finally {
      loading.finish();
    }
  }

  private startRouting() {

    if(isPlatformServer(this.platformId)){return}
    const pathname = decodeURIComponent(location.pathname);
    const params = new URLSearchParams(location.search);
    if(pathname.indexOf('funnel') === -1){
      if(isPlatformBrowser(this.platformId) && sessionStorage.getItem('prodottiCorrelati')){
        sessionStorage.removeItem('prodottiCorrelati');
      }
    }
    if (isPlatformBrowser(this.platformId)) {
      const obj = Object.fromEntries(params.entries()); // TODO Verificare corretto funzionamento
      const isFunnel = pathname.indexOf('funnel') > -1;
      const hasPdvParams = !!(obj.codicePDV || obj.trkCode || obj.customerId || obj.pdv || obj.QSCustomerId);
      this.startupService.load().subscribe({
        next: (res: any) => {
        if(res === true || res === false){
          if (isFunnel) { // TODO Verificare se questa modifica non entroduci ulteriori anomalia
            this.navigationService.getCacheData()
              .then(async () => {

                let pdvCache = await this.navigationService.getStoredItem('PdvCode');

                if (hasPdvParams) {
                  if (pdvCache) {
                    obj['codicePDV'] = pdvCache.codicePdv
                  }
                  this.navigationService.setStoredItem('PdvCode', {
                    pdvCode: obj?.codicePDV ? obj?.codicePDV : obj?.pdv,
                    trackingCode: obj?.trkCode,
                    QSCustomerId: obj?.customerId ? obj?.customerId : obj?.QSCustomerId
                  });
                }
            }).catch((e) => {
              console.debug('app component', e);
            });
          } else if (hasPdvParams) {
            void this.navigationService.setStoredItem('PdvCode', {
              pdvCode: obj?.codicePDV ? obj?.codicePDV : obj?.pdv,
              trackingCode: obj?.trkCode,
              QSCustomerId: obj?.customerId ? obj?.customerId : obj?.QSCustomerId
            });
          }
        }
      },
      error: () => {},
      });


      //pagamenti return url di BlackBox
      if (obj.hasOwnProperty('IDPAGAMENTO') && obj.hasOwnProperty('CODICECIRCUITO') && decodeURIComponent(location.href) !== '/pay-by-link') { // TODO VERIFICARE this.router.url
        void this.handlePaymentReturn(obj, 'pagamento con CC');
        return;
      }

//pagamenti return url di Satispay
      if (obj.hasOwnProperty('payment_id')) {

        void this.handlePaymentReturn(obj, 'pagamento con Satispay, Scalapay');
        return;
      }

      if (obj.hasOwnProperty('invitationCode') || obj.hasOwnProperty('registrazione') || obj.hasOwnProperty('validationCode')) {
        // TODO this.stateService.go(this.genericService.viewRoot() + '.ContentPage', { slugPath: location.pathname + location.search });
        return;
      }

//pagamenti return url di Amazon Pay
      if (obj.hasOwnProperty('AuthenticationStatus')) {
        if (obj.AuthenticationStatus === 'Success') {
          void this.handlePaymentReturn(obj, 'pagamento con Amazon Pay', true);
        }
        return;
      }

      if (obj.hasOwnProperty('rate')) {
        setTimeout(() => {
          this.loadQuoting(obj.rate, obj.trkCode, obj.pdv, obj.monthly);
        });
        return;
      }

      if (obj.hasOwnProperty('f') && obj.hasOwnProperty('p')) {
        setTimeout(() => {
          this.loadMyClinic(obj.f, obj.p, obj.trkCode, obj.QSCustomerId);
        });
        return;
      }
      if (obj.hasOwnProperty('f')) {
        setTimeout(() => {
          this.loadDigitaleSicuro(obj.f, obj.trkCode);
        });
        return;
      }
    }

    this.slug = decodeURIComponent(location.href); // TODO VERIFICARE router.url
    if (this.slug.indexOf('/funnel/policy-expired-date') === -1) {
      //this.router.navigate([this.slug + location.search], {onSameUrlNavigation: 'reload'});
      // TODO this.stateService.go(this.genericService.viewRoot() + '.ContentPage',
      // { slugPath: this.slug + location.search }, {reload: true, notify: true});
     } else {
      const ratenum = location.search.substr(9, location.search.length);
      this.router.navigate(['/funnel/policy-expired-date'], {queryParams: {ratenum: ratenum}});
      // TODO this.stateService.go('Funnel.PolizaRetroDatata', { ratenum });
    }
  }

  ngOnInit() {
    if(isPlatformBrowser(this.platformId)) {
      const CUSTOMER_INSIGHTS_JOURRNEY = this.document.createElement('script');
      CUSTOMER_INSIGHTS_JOURRNEY.type = 'text/javascript';
      CUSTOMER_INSIGHTS_JOURRNEY.text = `
         ( function ( a, t, i ) { var e = "MSCI"; var s = "Analytics"; var o = e + "queue"; a[o] = a[o] || []; var r = a[e] || function ( n ) { var t = {}; t[s] = {}; function e( e ) { while ( e.length ) { var r = e.pop(); t[s][r] = function ( e ) { return function () { a[o].push( [e, n, arguments] ); }; }( r ); } } var r = "track"; var i = "set"; e( [r + "Event", r + "View", r + "Action", i + "Config", i + "Property", i + "User", "initialize", "teardown"] ); return t; }( i.name ); var n = i.name; if ( !a[e] ) { a[n] = r[s]; a[o].push( ["new", n] ); setTimeout( function () { var e = "script"; var r = t.createElement( e ); r.async = 1; r.src = i.src; var n = t.getElementsByTagName( e )[0]; n.parentNode.insertBefore( r, n ); }, 1 ); } else { a[n] = new r[s]; } if ( i.user ) { a[n].setUser( i.user ); } if ( i.props ) { for ( var c in i.props ) { a[n].setProperty( c, i.props[c] ); } } a[n].initialize( i.cfg ); } )( window, document, {

      src: "https://cxppeur1rdrect01sa02cdn-endpoint.azureedge.net/webtracking/WebTracking/WebTracking.bundle.js",
      name: "MSCI",
      cfg: {

        ingestionKey: \`${this.configEnv.INGESTION_KEY}\`,
        endpointUrl: "https://mobile.events.data.microsoft.com/OneCollector/1.0/",
        autoCapture: {
          view: true,
          click: true
        },
        orgInfo: {
          orgId: "7b8cc5b2-4852-ee11-94d2-000d3ab4ed48",
          orgTenantId: "9f9d6315-bfeb-44e0-a998-39eae439fbc8",
          orgGeo: "EUR"
        }
      }
    } );
     `
      const MSDYNMKT = this.document.createElement('script');
      MSDYNMKT.type = 'text/javascript';
      MSDYNMKT.text = `
         ( function ( a, t, i ) { var e = "MSEI"; var s = "Analytics"; var o = e + "queue"; a[o] = a[o] || []; var r = a[e] || function ( n ) { var t = {}; t[s] = {}; function e( e ) { while ( e.length ) { var r = e.pop(); t[s][r] = function ( e ) { return function () { a[o].push( [e, n, arguments] ); }; }( r ); } } var r = "track"; var i = "set"; e( [r + "Event", r + "View", r + "Action", i + "Property", i + "User", "initialize", "teardown"] ); return t; }( i.name ); var n = i.name; if ( !a[e] ) { a[n] = r[s]; a[o].push( ["new", n] ); setTimeout( function () { var e = "script"; var r = t.createElement( e ); r.async = 1; r.src = i.src; var n = t.getElementsByTagName( e )[0]; n.parentNode.insertBefore( r, n ); }, 1 ); } else { a[n] = new r[s]; } if ( i.user ) { a[n].setUser( i.user ); } if ( i.props ) { for ( var c in i.props ) { a[n].setProperty( c, i.props[c] ); } } a[n].initialize( i.cfg ); } )( window, document, {
      src: "https://download.pi.dynamics.com/sdk/web/msei-0.js",
      name: "msdynmkt",
      cfg: {
        ingestionKey: \`${this.configEnv.INGESTION_KEY}\`
      }
    } );
      `;
      this.document.head.appendChild(MSDYNMKT);
      this.document.head.appendChild(CUSTOMER_INSIGHTS_JOURRNEY);

      this.checkLandingPageAndQueryParameter();
    }

    this.updateStickyVisibility();
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.updateStickyVisibility(event.urlAfterRedirects);
        this.gtmDatalayerService.trackPage();
      });

    this.startRouting();
    // TODO da fixare, errore sulla console di server
    setTimeout(() => this.gtmDatalayerService.trackUser(), 500);
    // TODO const data = sessionStorage.getItem('openedPage');
    if (isPlatformBrowser(this.platformId) && !sessionStorage.getItem('openedPage')  && location.href.indexOf('offer') === -1) {

      // TODO pdvCode era null che risultava in KO la chiamata, è giusto? ( ho settato il pdvCode online as default)
      void this.navigationService.setStoredItem('PdvCode', {
        pdvCode: 500801,
        trackingCode: null,
        QSCustomerId: null
      }).catch(() => {});
      sessionStorage.setItem('openedPage', 'open');
    }
  }

  onScrollToTop(): void {
    if (window.location.pathname.split('/')[1] === 'prodotti-per-te') {
      window.scrollTo({ top: 250, behavior: 'smooth' });
    } else if (window.location.pathname === '/') {
      window.scrollTo({ top: 230, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 250, behavior: 'smooth' });
    }
  }

  checkLandingPageAndQueryParameter() {
    const params = new URLSearchParams( window.location.search );
    const value = params.get( "utm_content" );
    if ( window.location.pathname.includes( '/landing-page' ) && value ) {
      console.info( 'Query Parameter UTM_CONTENT' );

      //Titolo trigger: Click da DEM
      this.consentOrchestrator.trackSecureEvent(
        {
          name: this.configEnv.CLICK_DA_DEM,
          properties: {
            "content": value,
            "bindingid": ""
          }
        }
      )



    }
  }
}
